import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Paper,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Build as WrenchIcon,
  Security as ShieldIcon,
  CurrencyRupee as IndianRupeeIcon,
  ConfirmationNumber as TicketIcon,
  ArrowBack as ArrowLeftIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import InstallationTab from "./InstallationTab";
import ServiceTab from "./ServiceTab";
import SalesTab from "./SalesTab";
import TicketsTab from "./TicketsTab";

import {
  InstallationEditModal,
  ServiceEditModal,
  SalesEditModal,
} from "./EditModals";

import {
  GET_DASHBOARD_STATS_ENDPOINT,
  GET_SEARCH_TICKETS_ENDPOINT,
  PUT_INSTALLATION_ENDPOINT,
  PUT_SERVICE_ENDPOINT,
  PUT_SALES_ENDPOINT,
  GET_RECENT_ACTIVITIES_ENDPOINT,
} from "../../config/urls";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import TableSearchInput from "../TableSearchInput";

// How long (ms) to keep the success message visible in a modal before
// auto-closing it. Change this in one place if the desired UX timing changes.
const MODAL_AUTO_CLOSE_DELAY_MS = 1500;

const DashboardCard = ({ icon: Icon, title, count, color, onClick }) => (
  <Card
    sx={{
      cursor: "pointer",
      transition: "all 0.2s",
      "&:hover": {
        transform: "scale(1.02)",
        boxShadow: 6,
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: "8px 14px !important" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            sx={{ ...typography.h1, fontSize: "0.75rem", mb: 0.25, color: "#505050" }}
            gutterBottom
          >
            {title}
          </Typography>
          <Typography sx={{ ...typography.statLarge, fontSize: "1.3rem", mb: 0.25 }}>
            {count}
          </Typography>
          <Typography
            sx={{ ...typography.bodyLarge, fontSize: "0.7rem", mb: 0 }}
            color="text.secondary"
          >
            Active items
          </Typography>
        </Box>
        <Box
          sx={{
            p: 0.75,
            borderRadius: "50%",
            backgroundColor: color,
            color: "white",
            height: 36,
            width: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const tabLabels = ["Installation", "Service", "Sales", "Tickets"];

export default function DoorLocksTab({
  onNavigate,
  activeTab: propActiveTab = 0,
}) {
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [activeTab, setActiveTab] = useState(propActiveTab);

  // Search functionality states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Modal states
  const [installationModalOpen, setInstallationModalOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);

  // Form data states for each modal
  const [installationFormData, setInstallationFormData] = useState({
    ticket_code: "",
    customer_name: "",
    customer_phone: "",
    wa_number: "",
    wa_name: "",
    door_type: "",
    lock_type: "",
    lock_type_others: "",
    door_installed: false,
    status: "",
    notes: "",
    address: {
      flat: "",
      street: "",
      area: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
    },
  });

  const [serviceFormData, setServiceFormData] = useState({
    ticket_code: "",
    serial_number: "",
    customer_phone: "",
    wa_number: "",
    customer_name: "",
    wa_name: "",
    issue_type: "",
    lock_type: "",
    service_status: "",
    issue_description: "",
    requirements: "",
    address: {
      flat: "",
      street: "",
      area: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
    },
  });

  const [salesFormData, setSalesFormData] = useState({
    ticket_code: "",
    customer_phone: "",
    wa_number: "",
    customer_name: "",
    wa_name: "",
    door_type: "",
    quantity: 1,
    sales_status: "",
    address: {
      flat: "",
      street: "",
      area: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
    },
    features: {
      fingerprint_access: false,
      rfid_card_access: false,
      pin_code_access: false,
      mechanical_key_access: false,
      mobile_app_access: false,
      video_access: false,
    },
  });

  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Add a handler for tab change that works with or without onNavigate
  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    setCurrentPage("details");
    if (onNavigate) {
      // Map tab index to sidebar keys
      const tabKeyMap = [
        "installation",
        "service",
        "sales",
        "billing_dashboard",
      ]; // 51: Installation, 52: Service, 53: Sales, 54: Tickets (if any)
      onNavigate(tabKeyMap[newValue] || "installation");
    }
  };

  // Handler for back button
  const handleBack = () => {
    setCurrentPage("dashboard");
    if (onNavigate) onNavigate("insights"); // 50 is the dashboard key
  };

  // Add state for dashboard stats
  const [stats, setStats] = useState(null); // null means "not loaded yet"
  const [loadingStats, setLoadingStats] = useState(true);

  // Reusable fetcher for dashboard/insights stats — used on mount AND after
  // any successful Installation/Service/Sales update so counts stay in sync.
  const fetchDashboardStats = useCallback(() => {
    setLoadingStats(true);
    return fetch(GET_DASHBOARD_STATS_ENDPOINT, {
      headers: { "X-Tenant-ID": "dormakaba" },
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, []);

  // Fetch dashboard stats on mount
  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Reusable fetcher for Recent Activity — used on mount AND after any
  // successful Installation/Service/Sales update so the list stays in sync.
  const fetchRecentActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const response = await fetch(GET_RECENT_ACTIVITIES_ENDPOINT, {
        headers: {
          "X-Tenant-ID": localStorage.getItem("tenant"),
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setRecentActivities(data);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  // Fetch recent activities on mount
  useEffect(() => {
    fetchRecentActivities();
  }, [fetchRecentActivities]);

  // Use API stats for counts
  const counts =
    loadingStats || !stats
      ? { installation: "-", service: "-", sales: "-", tickets: "-" }
      : {
          installation: stats.total_installations,
          service: stats.total_services,
          sales: stats.total_sales,
          tickets: stats.total_tickets,
        };

  // Pass onNavigate and activeTab to feature tabs
  const getTabComponent = () => {
    switch (activeTab) {
      case 0:
        onNavigate("installation");
        return <InstallationTab onNavigate={onNavigate} />;
      case 1:
        onNavigate("service");
        return <ServiceTab onNavigate={onNavigate} />;
      case 2:
        onNavigate("sales");
        return <SalesTab onNavigate={onNavigate} />;
      case 3:
        // return <TicketsTab onNavigate={onNavigate} setCurrentPage={setCurrentPage} />;
        return null;
      default:
        return null;
    }
  };

  // Search ticket function.
  // Accepts an optional ticketCode so it can be reused by the Recent
  // Activity "Edit" action; falls back to the search bar's searchQuery
  // when called with no argument (e.g. from the Search button/Enter key).
  const searchTicket = async (ticketCode) => {
    const code = (ticketCode ?? searchQuery).trim();
    if (!code) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${GET_SEARCH_TICKETS_ENDPOINT}${code}/`,
        {
          headers: {
            "X-Tenant-ID": localStorage.getItem("tenant"),
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("No ticket found with this ID");
        }
        throw new Error("Failed to search ticket");
      }

      const data = await response.json();

      // Determine ticket type and open appropriate modal
      if (data.installation_id) {
        // Installation ticket
        setInstallationFormData({
          installation_id: data.installation_id,
          ticket_code: data.ticket_code,
          customer_name: data.customer_name,
          customer_phone: data.customer_phone,
          wa_number: data.wa_number,
          wa_name: data.wa_name,
          door_type: data.door_type,
          lock_type: data.lock_type,
          lock_type_others: data.lock_type_others || "",
          door_installed: data.door_installed,
          status: data.status,
          notes: data.notes || "",
          address: { ...data.address_details },
        });
        setInstallationModalOpen(true);
      } else if (data.service_id) {
        // Service ticket
        setServiceFormData({
          service_id: data.service_id,
          ticket_code: data.ticket_code,
          serial_number: data.serial_number,
          customer_phone: data.customer_phone,
          wa_number: data.wa_number,
          customer_name: data.customer_name,
          wa_name: data.wa_name,
          issue_type: data.issue_type,
          lock_type: data.lock_type,
          service_status: data.service_status,
          issue_description: data.issue_description || "",
          requirements: data.requirements || "",
          address: { ...data.address_details },
        });
        setServiceModalOpen(true);
      } else if (data.sales_id) {
        // Sales ticket
        setSalesFormData({
          sales_id: data.sales_id,
          ticket_code: data.ticket_code,
          customer_phone: data.customer_phone,
          wa_number: data.wa_number,
          customer_name: data.customer_name,
          wa_name: data.wa_name,
          door_type: data.door_type,
          quantity: data.quantity,
          sales_status: data.sales_status,
          address: { ...data.address_details },
          features: {
            fingerprint_access: data.fingerprint_access,
            rfid_card_access: data.rfid_card_access,
            pin_code_access: data.pin_code_access,
            mechanical_key_access: data.mechanical_key_access,
            mobile_app_access: data.mobile_app_access,
            video_access: data.video_access,
          },
        });
        setSalesModalOpen(true);
      }
    } catch (error) {
      setSearchError(error.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search input key press
  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      searchTicket();
    }
  };

  // Handle form input changes for each modal
  const handleInstallationInputChange = (field, value) => {
    setInstallationFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInstallationAddressChange = (field, value) => {
    setInstallationFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleServiceInputChange = (field, value) => {
    setServiceFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleServiceAddressChange = (field, value) => {
    setServiceFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleSalesInputChange = (field, value) => {
    setSalesFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSalesAddressChange = (field, value) => {
    setSalesFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleSalesFeatureChange = (feature, value) => {
    setSalesFormData((prev) => ({
      ...prev,
      features: { ...prev.features, [feature]: value },
    }));
  };

  // Update functions for each modal
  const handleInstallationUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${PUT_INSTALLATION_ENDPOINT}${installationFormData.installation_id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": localStorage.getItem("tenant"),
          },
          body: JSON.stringify(installationFormData),
        },
      );

      if (response.ok) {
        setUpdateError(null);
        setUpdateSuccess(true);
        // Refresh Recent Activity / Insights immediately so updated values
        // show right away (Jira requirement: "updated values should appear
        // immediately").
        fetchRecentActivities();
        fetchDashboardStats();
        // Keep the existing success message visible briefly, then auto-close
        // the modal (Jira requirement: close modal automatically, while
        // still preserving the success message).
        setTimeout(() => {
          setInstallationModalOpen(false);
          setUpdateSuccess(false);
        }, MODAL_AUTO_CLOSE_DELAY_MS);
      } else {
        const data = await response.json();
        let errorMessages = [];
        if (data?.ticket?.length) {
          errorMessages.push(`Ticket Error: ${data.ticket.join(" ")}`);
        }
        if (data?.address?.length) {
          errorMessages.push(`Address Error: ${data.address.join(" ")}`);
        }
        if (data?.customer?.length) {
          errorMessages.push(`Customer Error: ${data.customer.join(" ")}`);
        }
        if (data?.customer_phone?.length) {
          errorMessages.push(`Phone Error: ${data.customer_phone.join(" ")}`);
        }
        if (data?.customer_name?.length) {
          errorMessages.push(`Name Error: ${data.customer_name.join(" ")}`);
        }
        if (data?.lock_type?.length) {
          errorMessages.push(`Lock Type Error: ${data.lock_type.join(" ")}`);
        }
        if (data?.status?.length) {
          errorMessages.push(`Status Error: ${data.status.join(" ")}`);
        }

        setUpdateError(
          errorMessages.length
            ? errorMessages.join(" | ")
            : "Failed to update installation. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error updating installation:", error.message);
      setUpdateError("Failed to update installation. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleServiceUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${PUT_SERVICE_ENDPOINT}${serviceFormData.service_id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": localStorage.getItem("tenant"),
          },
          body: JSON.stringify(serviceFormData),
        },
      );

      if (response.ok) {
        setUpdateError(null);
        setUpdateSuccess(true);
        // Refresh Recent Activity / Insights immediately so updated values
        // show right away (Jira requirement: "updated values should appear
        // immediately").
        fetchRecentActivities();
        fetchDashboardStats();
        // Keep the existing success message visible briefly, then auto-close
        // the modal (Jira requirement: close modal automatically, while
        // still preserving the success message).
        setTimeout(() => {
          setServiceModalOpen(false);
          setUpdateSuccess(false);
        }, MODAL_AUTO_CLOSE_DELAY_MS);
      } else {
        const data = await response.json();
        let errorMessages = [];
        if (Array.isArray(data)) {
          errorMessages.push(data.join(" "));
        }
        if (data?.ticket?.length) {
          errorMessages.push(`Ticket Error: ${data.ticket.join(" ")}`);
        }
        if (data?.address?.length) {
          errorMessages.push(`Address Error: ${data.address.join(" ")}`);
        }
        if (data?.customer_phone?.length) {
          errorMessages.push(`Phone Error: ${data.customer_phone.join(" ")}`);
        }
        if (data?.customer_name?.length) {
          errorMessages.push(`Name Error: ${data.customer_name.join(" ")}`);
        }
        if (data?.issue_type?.length) {
          errorMessages.push(`Issue Type Error: ${data.issue_type.join(" ")}`);
        }
        if (data?.lock_type?.length) {
          errorMessages.push(`Lock Type Error: ${data.lock_type.join(" ")}`);
        }
        if (data?.service_status?.length) {
          errorMessages.push(
            `Service Status Error: ${data.service_status.join(" ")}`,
          );
        }
        if (data?.issue_description?.length) {
          errorMessages.push(
            `Issue Description Error: ${data.issue_description.join(" ")}`,
          );
        }

        setUpdateError(
          errorMessages.length
            ? errorMessages.join(" | ")
            : "Failed to update service. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error updating service:", error.message);
      setUpdateError("Failed to update service. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSalesUpdate = async () => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${PUT_SALES_ENDPOINT}${salesFormData.sales_id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": localStorage.getItem("tenant"),
          },
          body: JSON.stringify(salesFormData),
        },
      );

      if (response.ok) {
        setUpdateError(null);
        setUpdateSuccess(true);
        // Refresh Recent Activity / Insights immediately so updated values
        // show right away (Jira requirement: "updated values should appear
        // immediately").
        fetchRecentActivities();
        fetchDashboardStats();
        // Keep the existing success message visible briefly, then auto-close
        // the modal (Jira requirement: close modal automatically, while
        // still preserving the success message).
        setTimeout(() => {
          setSalesModalOpen(false);
          setUpdateSuccess(false);
        }, MODAL_AUTO_CLOSE_DELAY_MS);
      } else {
        const data = await response.json();
        let errorMessages = [];
        if (data?.ticket?.length) {
          errorMessages.push(`Ticket Error: ${data.ticket.join(" ")}`);
        }
        if (data?.address?.length) {
          errorMessages.push(`Address Error: ${data.address.join(" ")}`);
        }
        if (data?.customer_phone?.length) {
          errorMessages.push(`Phone Error: ${data.customer_phone.join(" ")}`);
        }
        if (data?.customer_name?.length) {
          errorMessages.push(`Name Error: ${data.customer_name.join(" ")}`);
        }
        if (data?.door_type?.length) {
          errorMessages.push(`Door Type Error: ${data.door_type.join(" ")}`);
        }
        if (data?.quantity?.length) {
          errorMessages.push(`Quantity Error: ${data.quantity.join(" ")}`);
        }
        if (data?.sales_status?.length) {
          errorMessages.push(
            `Sales Status Error: ${data.sales_status.join(" ")}`,
          );
        }

        setUpdateError(
          errorMessages.length
            ? errorMessages.join(" | ")
            : "Failed to update sales. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error updating sales:", error.message);
      setUpdateError("Failed to update sales. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const getActivityConfig = (ticketType, status) => {
    const config = {
      Installation: { icon: WrenchIcon, color: "#1976d2", bgColor: "#e3f2fd" },
      Service: { icon: ShieldIcon, color: "#388e3c", bgColor: "#e8f5e8" },
      Sales: { icon: IndianRupeeIcon, color: "#7b1fa2", bgColor: "#f3e5f5" },
      Tickets: { icon: TicketIcon, color: "#f57c00", bgColor: "#fff3e0" },
    };

    return (
      config[ticketType] || {
        icon: TicketIcon,
        color: "#757575",
        bgColor: "#f5f5f5",
      }
    );
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Utility function
  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (currentPage === "dashboard") {
    return (
      <Box sx={{ minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 1.5 }}>
          <Box textAlign="center" mb={1.5}>
            <Typography sx={{ ...typography.statLarge, fontSize: "1.35rem" }} mb={0.25}>
              SecureDoor Solutions
            </Typography>
            <Typography sx={{ ...typography.h1, fontSize: "0.85rem", fontWeight: 400 }} color="text.secondary">
              Professional Door Lock Services Dashboard
            </Typography>
          </Box>
          {/* Loader for stats */}
          {loadingStats ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={1.5} justifyContent="center" mb={1.75}>
                <Grid item xs={12} sm={6} md={3}>
                  <DashboardCard
                    icon={WrenchIcon}
                    title="Installation"
                    count={counts.installation}
                    color="#1976d2"
                    onClick={() => {
                      setActiveTab(0);
                      setCurrentPage("details");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DashboardCard
                    icon={ShieldIcon}
                    title="Service"
                    count={counts.service}
                    color="#388e3c"
                    onClick={() => {
                      setActiveTab(1);
                      setCurrentPage("details");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DashboardCard
                    icon={IndianRupeeIcon}
                    title="Sales"
                    count={counts.sales}
                    color="#7b1fa2"
                    onClick={() => {
                      setActiveTab(2);
                      setCurrentPage("details");
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DashboardCard
                    icon={TicketIcon}
                    title="Tickets"
                    count={counts.tickets}
                    color="#f57c00"
                    // TODO: Tickets feature is hidden for now
                    // onClick={() => { setActiveTab(3); setCurrentPage('details'); }}
                  />
                </Grid>
              </Grid>

              {/* Search Section */}
              <Card sx={{ mb: 1.75 }}>
                <CardContent sx={{ py: "10px !important" }}>
                  <Typography variant="h6" fontWeight="bold" mb={1}>
                    Search Tickets
                  </Typography>
                  {searchError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      onClose={() => setSearchError(null)}
                    >
                      {searchError}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 450 }}>
                      <TableSearchInput
                        searchQuery={searchQuery}
                        onSearchChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                        placeholder="Search by Ticket ID (e.g., DL281125208, DL281125209)"
                        loading={searchLoading}
                        fullWidth
                      />
                    </Box>
                    <ButtonOne
                      variant="contained"
                      onClick={() => searchTicket()}
                      disabled={searchLoading || !searchQuery.trim()}
                    >
                      Search Ticket
                    </ButtonOne>
                  </Box>
                </CardContent>
              </Card>
              {/* Recent Activity */}
              <Typography sx={{ ...typography.h1, fontSize: "1rem" }} mb={1}>
                Recent Activity
              </Typography>
              <Card>
                <CardContent sx={{ py: "8px !important" }}>
                  {loadingActivities ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={4}
                    >
                      <CircularProgress />
                    </Box>
                  ) : recentActivities.length === 0 ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={4}
                    >
                      <Typography color="text.secondary">
                        No recent activities found
                      </Typography>
                    </Box>
                  ) : (
                    recentActivities.slice(0, 5).map((activity, index) => {
                      const {
                        icon: ActivityIcon,
                        color,
                        bgColor,
                      } = getActivityConfig(
                        activity.ticket_type,
                        activity.status,
                      );
                      return (
                        <Box key={activity.ticket_id || index} mb={0.75}>
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            sx={{
                              p: 1,
                              borderLeft: `4px solid ${color}`,
                              backgroundColor: bgColor,
                              borderRadius: "0 8px 8px 0",
                            }}
                          >
                            <Box
                              sx={{
                                p: 0.75,
                                borderRadius: "50%",
                                backgroundColor: color,
                                color: "white",
                                mr: 1.25,
                                height: 22,
                                width: 22,
                                display: "flex",           // ← add
                                alignItems: "center",      // ← add
                                justifyContent: "center",  // ← add
                                flexShrink: 0,             // ← prevents squishing in flex rows
                              }}
                            >
                              <ActivityIcon sx={{ fontSize: 12 }} />
                            </Box>
                            <Box flex={1}>
                              <Typography sx={{ ...typography.bodyLarge, fontSize: "0.8rem" }}>
                                {/* Title case */}
                                {toTitleCase(activity.ticket_type)} -{" "}
                                {toTitleCase(activity.status)}
                              </Typography>
                              <Typography
                                sx={{ ...typography.bodyLarge, fontSize: "0.73rem" }}
                                color="text.secondary"
                              >
                                {activity.customer_name} -{" "}
                                {activity.ticket_code}
                                {activity.priority &&
                                  ` • Priority: ${activity.priority}`}
                              </Typography>
                              <Typography
                                sx={{ ...typography.bodySmall, fontSize: "0.68rem" }}
                                color="text.secondary"
                              >
                                {formatTimeAgo(activity.updated_at)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              aria-label="Edit ticket"
                              disabled={searchLoading}
                              onClick={() =>
                                searchTicket(activity.ticket_code)
                              }
                              sx={{ ml: 1, flexShrink: 0 }}
                            >
                              <EditIcon
                                sx={{ fontSize: 24, color: "#000000" }}
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Container>

        {/* Installation Edit Modal */}
        <InstallationEditModal
          open={installationModalOpen}
          onClose={() => {
            setInstallationModalOpen(false);
            setUpdateError(null);
            setUpdateSuccess(false);
          }}
          formData={installationFormData}
          onInputChange={handleInstallationInputChange}
          onAddressChange={handleInstallationAddressChange}
          onUpdate={handleInstallationUpdate}
          loading={updateLoading}
          error={updateError}
          success={updateSuccess}
        />

        {/* Service Edit Modal */}
        <ServiceEditModal
          open={serviceModalOpen}
          onClose={() => {
            setServiceModalOpen(false);
            setUpdateError(null);
            setUpdateSuccess(false);
          }}
          formData={serviceFormData}
          onInputChange={handleServiceInputChange}
          onAddressChange={handleServiceAddressChange}
          onUpdate={handleServiceUpdate}
          loading={updateLoading}
          error={updateError}
          success={updateSuccess}
        />

        {/* Sales Edit Modal */}
        <SalesEditModal
          open={salesModalOpen}
          onClose={() => {
            setSalesModalOpen(false);
            setUpdateError(null);
            setUpdateSuccess(false);
          }}
          formData={salesFormData}
          onInputChange={handleSalesInputChange}
          onAddressChange={handleSalesAddressChange}
          onFeatureChange={handleSalesFeatureChange}
          onUpdate={handleSalesUpdate}
          loading={updateLoading}
          error={updateError}
          success={updateSuccess}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="100" sx={{ py: 4 }}>
        {getTabComponent()}
      </Container>
    </Box>
  );
}